import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { H4, TextBody, TextTitle } from '@/design-system/components/typography';
import { SxProp, View, Image } from 'dripsy';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { LockCardStackParamList, LockCardStackRoutes } from './type';
import { Card } from '../../../../../../packages/utx-access-client/src/react';
import { AlertType, UniAlert } from '@/design-system/components/alerts';

export interface UnlockCardProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  account: string;
  tab?: number;
  onType: (value: string | undefined) => void;
  type: string;
  record?: Card;
  func?: (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  actionResponse?: any;
  actionError?: any;
  onData?: (value: any) => void;
  refresh?: () => void;
}

export const UnlockCardWebZeroScreen: React.FC<UnlockCardProps> = (props) => {
  const { onClose, onLayout, account, tab, onType, type, record, func, actionResponse, actionError, onData, refresh } = props;
  const { t } = useTranslation('account-management');
  const { topHeader, body, textMb, imageSx, btnView, btn, textGroup } = sxStyles;
  const layout = useContext(LayoutContext);
  const [loading, setLoading] = useState(false);
  const navigation = useNavigation<NativeStackNavigationProp<LockCardStackParamList>>();
  const [alertMessage, setAlertMessage] = useState('');

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <View sx={topHeader}>
        <ModalHeader title={t('card-account-management::unlock-card.title--unlock-instruction-confirmation')} closeModal={onClose} />
      </View>
      <View sx={body}>
        <TextBody sx={textMb} semiBold>
          {t('card-account-management::unlock-card.text--unlock-following-card')}
        </TextBody>
        <View sx={{ alignItems: 'center' }}>
          <Image sx={imageSx} source={record?.product?.imageUrl || require('assets/images/worldcard_blank.png')} />
          <View sx={textGroup}>
            <View sx={{ gap: '$1' }}>
              <H4 semiBold sx={{ color: '$white' }}>
                {record?.embossedName}
              </H4>
              <TextTitle sx={{ color: '$white' }}>**** **** **** {record?.last4}</TextTitle>
            </View>
          </View>
        </View>
        {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
        <View sx={btnView}>
          <UniButton
            sx={btn}
            disabled={loading}
            variant={ButtonVariant.outline1}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::general.button--cancel')}
            onPress={onClose}
          />
          <UniButton
            sx={btn}
            loading={loading}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::general.button--place-instruction')}
            onPress={() => {
              if (func) {
                setLoading(true);
                func(
                  record?.id,
                  (data) => {
                    setLoading(false);
                    onType(type);
                    onData(data.data);
                    navigation.navigate(LockCardStackRoutes.Placed, {
                      onLayout: onLayout,
                      account,
                      tab,
                      type,
                      record,
                      data: data.data,
                      refresh
                    });
                  },
                  (error) => {
                    setLoading(false);
                    setAlertMessage(error?.message);
                  }
                );
              }
            }}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  topHeader: {
    height: 70,
    justifyContent: 'center',
    background: 'primaryDefault'
  },
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  imageSx: {
    width: 400,
    height: 252
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    mt: '$10'
  },
  btn: {
    width: 216,
    height: 44
  },
  textGroup: {
    position: 'absolute',
    top: 0,
    left: '$10',
    height: 242,
    paddingX: '$4',
    paddingTop: '$11',
    paddingBottom: '$5',
    gap: '$3',
    justifyContent: 'flex-end'
  }
};
